import React, { useEffect, useMemo, useState } from "react";
import { Flex, Spacer, Button } from "@chakra-ui/react";
import { RefreshIcon } from "@heroicons/react/outline";
import { compact } from "lodash";
import { Link, matchPath, useLocation } from "react-router-dom";
import useCheckApplicationUpdate from "../../../../hooks/useCheckApplicationUpdate";
import useTranslations from "../../../../hooks/useTranslations";
import useViewer from "../../../../hooks/useViewer";
import {
  AnalyticsIcon,
  BuildingIcon,
  DashboardIcon,
  IconProps,
  CampaignIcon,
  ReviewIcon,
} from "../../../icons";
import { SettingsIcon } from "../../../icons";
import Logo from "../../../Logo";
import UserAvatar from "../../../utils/avatars/UserAvatar";

type ProfileLinkProps = {
  onHide?: () => void;
};

const ProfileLink: React.FC<ProfileLinkProps> = ({ onHide }) => {
  const { avatar, name } = useViewer();

  return (
    <Link
      to="/profile"
      className="items-center px-4 py-4 text-light-900 focus:outline-none focus:bg-gray-100 focus:text-dark-100"
      onClick={onHide}
    >
      <div className="flex items-center">
        <UserAvatar size={8} src={avatar} alt={`${name}-avatar`} />
        <span className="ml-3 text-sm font-medium text-dark-100">{name}</span>
      </div>
    </Link>
  );
};

const RefreshApplicationButton: React.FC = () => {
  const t = useTranslations();

  return (
    <>
      <Spacer />
      <Flex>
        <Button
          leftIcon={<RefreshIcon className="w-3 h-3"/>}
          onClick={() => window.location.reload()}
          size="xs"
          mx={3}
          flex={1}
        >
          {t("applicationUpdate.button")}
        </Button>
      </Flex>
    </>
  )
};

type SidebarLinkProps = {
  id: string;
  to?: string;
  icon: React.FC<IconProps>;
  label: React.ReactNode;
  items: SidebarLinkProps[];
  onHide: () => void;
};

const SidebarLink: React.FC<SidebarLinkProps> = React.memo(
  ({ id, to, icon: Icon, label, items = [], onHide }) => {
    const location = useLocation();

    const isActive = (to) =>
      !!matchPath(location.pathname, { path: to, exact: false, strict: false });

    const isActiveDeep = (items) =>
      items.some(({ to, items = [] }) => isActive(to) || isActiveDeep(items));

    const [active, setActive] = useState(false);
    useEffect(() => {
      setActive(isActive(to) || isActiveDeep(items));
    }, [location.pathname, to, items]);

    const activeClassNames =
      "text-dark-300 group-hover:text-dark-300 group-hover:bg-gray-100 group-focus:text-dark-300 group-focus:bg-gray-100";
    const idleClassNames =
      "text-light-900 group-hover:text-dark-300 group-hover:bg-gray-100 group-focus:text-dark-300 group-focus:bg-gray-100";

    const activeIconClassNames = "text-primary-500";
    const idleIconClassNames = "text-light-900";

    const labelNode = (
      <div
        className={`flex flex-grow items-center px-3 py-3 rounded-lg ${
          active ? activeClassNames : idleClassNames
        }`}
      >
        {Icon ? (
          <Icon
            className={`w-6 h-6 mr-4 ${
              active ? activeIconClassNames : idleIconClassNames
            } transition duration-150 ease-in-out`}
          />
        ) : (
          <div className="w-6 h-6 mr-4" />
        )}
        {label}
      </div>
    );

    if (to) {
      return (
        <Link
          id={id}
          to={to}
          className={`w-full flex items-center px-2 border-r-2 border-transparent text-base md:text-sm font-medium leading-6 hover:no-underline ${
            active ? "border-primary-600" : ""
          } transition my-1 duration-150 ease-in-out group focus:outline-none`}
          onClick={onHide}
        >
          {labelNode}
        </Link>
      );
    }

    if (items.length === 0) {
      return null;
    }

    return (
      <>
        <button
          className={`w-full flex items-center px-2 border-r-2 border-transparent text-base md:text-sm font-medium leading-6 hover:no-underline transition my-1 duration-150 ease-in-out group focus:outline-none`}
          onClick={() => setActive((active) => !active)}
        >
          {labelNode}
        </button>
        <div className={!active ? "hidden" : ""}>
          {items.map(({ ...props }) => (
            <SidebarLink {...props} key={`${props.id}-${props.to}`} onHide={onHide}/>
          ))}
        </div>
      </>
    );
  }
);

type SidebarProps = { stripped: boolean; visible: boolean; onHide: () => void };

const Sidebar: React.FC<SidebarProps> = ({ stripped, visible, onHide }) => {
  const user = useViewer();
  const t = useTranslations();
  const isApplicationUpdated = useCheckApplicationUpdate();

  const schema = useMemo(
    () =>
      compact([
        user.isCapableOfReadingActivities && {
          id: "dashboardSidebar",
          to: "/dashboard",
          icon: DashboardIcon,
          label: t("header.dashboard"),
        },
        {
          id: "companySidebar",
          label: t("header.myCompany"),
          icon: BuildingIcon,
          items: compact([
            user.isCapableOfReadingUsers && {
              id: "usersSidebar",
              to: "/users",
              label: t("header.users"),
            },
            user.isCapableOfInsertingTemplates && {
              id: "templatesSidebar",
              to: "/templates",
              label: t("header.templates"),
            },
            user.isCapableOfReadingSkills && {
              id: "skillsSidebar",
              to: "/profession-skill-library",
              label: t("header.skills"),
            },
          ]),
        },
        user.isCapableOfReadingCampaigns && {
          id: "groupsSidebar",
          to: "/campaigns",
          icon: CampaignIcon,
          label: t("header.campaigns"),
        },
        user.isCapableOfReadingReviews &&
          !user.isCapableOfReadingCampaigns && {
            id: "groupsSidebar",
            to: "/reviews",
            icon: ReviewIcon,
            label: t("header.reviews"),
          },
        user.isCapableOfReadingUsers && {
          id: "statisticsSidebar",
          items: [
            {
              id: "usersSidebar",
              to: "/analytics/skill-mapping",
              label: t("header.skillMapping"),
            },
            {
              id: "templatesSidebar",
              to: "/analytics/evaluation-mapping",
              label: t("header.evaluationMapping"),
            },
            {
              id: "skillsSidebar",
              to: "/analytics/question-mapping",
              label: t("header.questionMapping"),
            },
          ],
          icon: AnalyticsIcon,
          label: t("header.statistics"),
        },
        user.isCapableOfUpdatingCompanies && {
          id: "configureCompanySidebar",
          to: "/company",
          icon: SettingsIcon,
          label: t("header.configuration"),
        },
      ]).map(({ ...props }) => (
        <SidebarLink {...props} key={`${props.label}-${props.to}`} onHide={onHide}/>
      )),
    [user]
  );

  const footerLinks = useMemo(
    () => [
      {
        label: t("footer.about"),
        to: { pathname: "https://www.klarahr.com/a-propos" },
      },
      {
        label: t("footer.blog"),
        to: { pathname: "https://www.klarahr.com/blog" },
      },
      {
        label: t("footer.privacyPolicy"),
        to: { pathname: " https://www.klarahr.com/politique-de-confidentialite" }
      },
      {
        label: t("footer.termsOfSale"),
        to: { pathname: "https://intercom.help/mooveo/en/articles/5411585-conditions-de-vente" }
      },
      {
        label: t("footer.termsAndConditions"),
        to: { pathname: "https://intercom.help/mooveo/en/articles/5411605-conditions-d-utilisation" }
      },
      {
        label: "Linkedin",
        to: { pathname: "https://www.linkedin.com/company/klarahr/" },
      },
    ],
    []
  );

  return (
    <>
      {/* <!-- Off-canvas menu for mobile --> */}
      <div className={`${stripped ? "hidden" : ""} md:hidden`}>
        <div
          className={`fixed inset-0 z-40 flex ${
            visible ? "visible" : "invisible"
          }`}
        >
          <div
            className={`fixed inset-0 transition-opacity ease-linear duration-300 ${
              visible ? "opacity-100" : "opacity-0"
            }`}
          >
            <div className="absolute inset-0 bg-gray-600 opacity-75"></div>
          </div>
          <div
            className={`relative transition ease-in-out duration-300 transform flex flex-col flex-1 w-full max-w-xs pt-5 pb-4 bg-white ${
              visible ? "translate-x-0" : "-translate-x-full"
            }`}
          >
            <div
              className={`absolute top-0 right-0 p-1 -mr-14 ${
                visible ? "" : "hidden"
              }`}
            >
              <button
                className="flex items-center justify-center w-12 h-12 rounded-full focus:outline-none focus:bg-gray-600"
                aria-label="Close sidebar"
                onClick={onHide}
              >
                <svg
                  className="w-6 h-6 text-white"
                  stroke="currentColor"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
            <div className="flex items-center flex-shrink-0 px-4">
              <Link 
                to="/"
                onClick={onHide}
              >
                <Logo width="8rem" />
              </Link>
            </div>
            <div className="flex-1 h-0 mt-5 overflow-y-auto">
              <nav className="space-y-1">
                {schema}
                {
                  isApplicationUpdated && (
                    <RefreshApplicationButton />
                  )
                }
              </nav>
            </div>
            <ProfileLink 
              onHide={onHide}
            />
          </div>
          <div className="flex-shrink-0 w-14"></div>
        </div>
      </div>
      {/* <!-- Static sidebar for desktop --> */}
      <div className="hidden md:flex md:flex-shrink-0 print:hidden">
        <div className="flex flex-col w-64">
          <div className="flex flex-col flex-grow pt-5 pb-4 overflow-y-auto bg-white border-r border-gray-200">
            <div className="flex items-center flex-shrink-0 px-7">
              <Link to="/">
                <Logo width="8rem" />
              </Link>
            </div>
            <div className="flex flex-col flex-grow mt-5">
              <nav className="flex-1 space-y-1">
                {schema}
                {
                  isApplicationUpdated && (
                    <RefreshApplicationButton />
                  )
                }
              </nav>
            </div>

            <ProfileLink />
            <div className="flex flex-row flex-wrap px-4 pt-4 border-t border-gray-200">
              {footerLinks.map(({ label, to }, index) => (
                <span
                  key={index}
                  className="text-xs text-light-900 whitespace-nowrap"
                >
                  <Link to={to} target="_blank">
                    {label}
                  </Link>
                  {index < footerLinks.length - 1 && (
                    <span className="px-2">{`·`}</span>
                  )}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Sidebar;
